import { CheckOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import '../../../../assets/style/taskFilter.css';
import usePrevious from '../../../../js/usePrevious';
import {
  getWorkspaceCustomFields,
  getWorkspaceProjectCustomFields,
  isWorkspaceProjectCustomFieldsLoaded,
} from '../../../../redux/workspace/selectors';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import TagIcon from '../home/TaskFilter/Icons/Tag';
import FilterPill from './FilterPill';
import { PRIORITY_COLORS, PRIORITY_ICON, PRIORITY_LABEL } from '../../../../js/constants';

// --- Filter Data Definition ---
const getFilters = (participants, tags, customFields) => {
  const customFieldOptions = customFields
    .map((cf) => ({
      key: cf._id,
      label: cf.name,
      icon: <img src="/images/icons/tag-filled.svg" alt="" className="size-4" />,
      options: cf.possibleValues.map((pv) => ({ key: pv._id, label: pv.label })),
    }))
    .filter((cfo) => cfo.options.length);

  return [
    {
      key: 'assignedTo',
      label: 'Assignee',
      icon: <img src="/images/icons/members-gray.svg" alt="" className="size-4" />,
      options: [
        { _id: 'unassigned', fullName: 'Unassigned', avi: 'pmc/public/users/global/no_avi.png' },
        ...participants,
      ].map((p) => ({
        key: p._id,
        label: p.fullName,
        icon: (
          <UserAvatar
            alt="User"
            classNames="w-5 h-5 rounded-full"
            fullName={p?.fullName}
            userId={p?._id}
            src={p?.avi}
            styles={{ objectFit: 'cover' }}
          />
        ),
      })),
    },
    {
      key: 'priority',
      label: 'Priority',
      icon: <img src="/images/icons/priority-gray.svg" alt="" className="size-4" />,
      options: Object.keys(PRIORITY_LABEL).map((priority) => ({
        key: priority,
        label: PRIORITY_LABEL[priority],
        color: PRIORITY_COLORS[priority],
        icon: <img src={`/images/icons/${PRIORITY_ICON[priority]}.svg`} alt="" className="size-3" />,
      })),
    },
    {
      key: 'status',
      label: 'Completion',
      icon: <CheckOutlined style={{ fontSize: '14px', color: '#9ca3af' }} />,
      options: [
        { key: 'completed-tasks', label: 'Completed' },
        { key: 'incomplete', label: 'Incomplete' },
      ],
      radio: true,
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      icon: <img src="/images/icons/calendar-gray.svg" alt="" className="size-4" />,
      options: [
        { key: 'today', label: 'Today' },
        { key: 'this-week', label: 'This Week' },
        { key: 'this-month', label: 'This Month' },
      ],
      radio: true,
    },
    {
      key: 'tags',
      label: 'Tag',
      icon: <TagIcon color="#9ca3af" />,
      options: tags.map((t) => ({ key: t._id, label: t.tag })),
    },
    {
      key: 'badge',
      label: 'Type',
      icon: <img src="/images/icons/tag-filled.svg" alt="" className="size-4" style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }} />,
      options: [
        { key: 'form', label: 'Form' },
        { key: 'subtask', label: 'Subtask' },
      ],
      radio: true,
    },
    ...customFieldOptions,
  ];
};

const defaultState = {
  assignedTo: [],
  priority: [],
  status: [],
  dueDate: [],
  tags: [],
  badge: [],
};

const FilterDropdown = ({
  onChange,
  participants,
  projectCustomFields,
  isProjectCustomFieldsLoaded,
  workspaceTags,
  workspaceCustomFields,
  isWorkspaceLevel = false,
  filtersToLoad,
}) => {
  const customFields = useMemo(
    () => (isWorkspaceLevel ? workspaceCustomFields : projectCustomFields),
    [isWorkspaceLevel, projectCustomFields, workspaceCustomFields],
  );
  const customFieldsLoaded = useMemo(
    () => (isWorkspaceLevel ? true : isProjectCustomFieldsLoaded),
    [isProjectCustomFieldsLoaded, isWorkspaceLevel],
  );

  const filters = useMemo(
    () => getFilters(participants, workspaceTags, customFields),
    [workspaceTags, participants, customFields],
  );

  const hasMounted = useRef(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [optionSearch, setOptionSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(filtersToLoad || defaultState);

  const isNonEmptyFilters = useMemo(
    () => Object.values(selectedFilters).some((f) => !isEmpty(f)),
    [selectedFilters],
  );
  const prevNonIsEmptyFilters = usePrevious(isNonEmptyFilters);

  // Click-away to close panel
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const openCategory = (filter) => {
    setActiveCategory(filter);
    setOptionSearch('');
    setIsOpen(true);
  };

  const toggleFilter = (category, value, parentKey) => {
    setSelectedFilters((prev) => {
      const values = (parentKey ? prev[category]?.[parentKey] : prev[category]) || [];
      const isSelected = values.includes(value);
      const newValues = isSelected ? values.filter((v) => v !== value) : [...values, value];
      return {
        ...prev,
        [category]: parentKey ? { ...(prev?.[category] || {}), [parentKey]: newValues } : newValues,
      };
    });
  };

  const selectSingle = (category, value, parentKey) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: parentKey ? { [parentKey]: [value] } : [value],
    }));
  };

  const handleOptionToggle = (categoryKey, optionKey, parentKey) => {
    const filter = filters.find((f) => f.key === categoryKey);
    if (filter?.radio) {
      selectSingle(categoryKey, optionKey, parentKey);
    } else {
      toggleFilter(categoryKey, optionKey, parentKey);
    }
  };

  const handleRemoveFilter = (key) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: [] }));
  };

  const handleClearAll = () => setSelectedFilters(defaultState);

  const isSelected = (categoryKey, optionKey, parentKey) => {
    const vals = parentKey
      ? selectedFilters[categoryKey]?.[parentKey]
      : selectedFilters[categoryKey];
    return Array.isArray(vals) ? vals.includes(optionKey) : false;
  };

  const getCategoryCount = (key) => {
    const val = selectedFilters[key];
    if (!val) return 0;
    if (Array.isArray(val)) return val.length;
    return Object.values(val).flat().length;
  };

  useEffect(() => {
    if (!isEmpty(filtersToLoad)) {
      setSelectedFilters(filtersToLoad);
    }
  }, [filtersToLoad]);

  useEffect(() => {
    if (customFieldsLoaded) {
      const customFieldIdsObj = customFields.reduce((acc, cf) => {
        acc[cf._id] = [];
        return acc;
      }, {});
      setSelectedFilters((prev) => ({ ...prev, ...customFieldIdsObj }));
      hasMounted.current = true;
    }
  }, [customFields, customFieldsLoaded]);

  useEffect(() => {
    if (!isNonEmptyFilters && !prevNonIsEmptyFilters) return;
    if (hasMounted.current || isWorkspaceLevel) {
      onChange(selectedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonEmptyFilters, prevNonIsEmptyFilters, selectedFilters]);

  // Build applied pill data
  const getAppliedPills = () => {
    const pills = [];

    const push = (key, label, values) => {
      if (!values.length) return;
      pills.push({ key, label, selectedValues: values });
    };

    const assignedToFilter = filters.find((f) => f.key === 'assignedTo');
    push(
      'assignedTo',
      'Assignee is',
      (selectedFilters.assignedTo || []).map((id) =>
        assignedToFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    const priorityFilter = filters.find((f) => f.key === 'priority');
    push(
      'priority',
      'Priority is',
      (selectedFilters.priority || []).map((id) =>
        priorityFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    const statusFilter = filters.find((f) => f.key === 'status');
    push(
      'status',
      'Completion is',
      (selectedFilters.status || []).map((id) =>
        statusFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    const dueDateFilter = filters.find((f) => f.key === 'dueDate');
    push(
      'dueDate',
      'Due Date is',
      (selectedFilters.dueDate || []).map((id) =>
        dueDateFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    const tagsFilter = filters.find((f) => f.key === 'tags');
    push(
      'tags',
      'Tag is',
      (selectedFilters.tags || []).map((id) =>
        tagsFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    const badgeFilter = filters.find((f) => f.key === 'badge');
    push(
      'badge',
      'Type is',
      (selectedFilters.badge || []).map((id) =>
        badgeFilter?.options.find((o) => o.key === id),
      ).filter(Boolean),
    );

    // Custom fields
    Object.keys(selectedFilters)
      .filter((k) => !['assignedTo', 'priority', 'status', 'dueDate', 'tags', 'badge'].includes(k))
      .forEach((k) => {
        const cf = customFields.find((f) => f._id === k);
        if (!cf) return;
        push(
          k,
          cf.name,
          (selectedFilters[k] || []).map((valueId) => {
            const pv = cf.possibleValues.find((p) => p._id === valueId);
            return pv ? { key: valueId, label: pv.label } : null;
          }).filter(Boolean),
        );
      });

    return pills;
  };

  const appliedPills = getAppliedPills();

  const filteredCategories = filters.filter((f) =>
    f.label?.toLowerCase().includes(categorySearch.toLowerCase()),
  );

  const activeOptions = useMemo(() => {
    if (!activeCategory) return [];
    return (activeCategory.options || []).filter((o) =>
      o.label?.toString().toLowerCase().includes(optionSearch.toLowerCase()),
    );
  }, [activeCategory, optionSearch]);

  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0 relative">
      {/* Filter button */}
      <button
        ref={buttonRef}
        type="button"
        className={`flex items-center gap-1.5 h-7 px-3 rounded-md border text-xs font-medium transition-all duration-150 flex-shrink-0 ${
          isNonEmptyFilters
            ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
        }`}
        onClick={() => setIsOpen((v) => !v)}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        Filters
        {isNonEmptyFilters && (
          <span className="inline-flex items-center justify-center size-4 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
            {appliedPills.length}
          </span>
        )}
      </button>

      {/* Custom two-column panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="absolute z-[1000] top-[calc(100%+6px)] left-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)', minWidth: 200 }}
        >
          {/* Left: categories */}
          <div style={{ width: 200 }}>
            <div className="p-2.5 border-b border-gray-100">
              <div className="relative">
                <svg className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Search filters…"
                  className="w-full pl-6 pr-2 h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white text-gray-700 placeholder-gray-400 transition-colors"
                />
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
              {filteredCategories.map((filter) => {
                const count = getCategoryCount(filter.key);
                const isActive = activeCategory?.key === filter.key;
                return (
                  <button
                    key={filter.key}
                    type="button"
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-[13px] transition-colors ${
                      isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => openCategory(filter)}
                  >
                    <span className="flex-shrink-0 flex items-center" style={{ opacity: isActive ? 1 : 0.6 }}>
                      {filter.icon}
                    </span>
                    <span className="flex-1 text-left font-[450]">{filter.label}</span>
                    {count > 0 && (
                      <span className="flex-shrink-0 text-[10px] font-semibold text-blue-600 bg-blue-100 rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {isNonEmptyFilters && (
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-[12px] text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors text-left"
                  onClick={handleClearAll}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Right: options */}
          {activeCategory && (
            <div className="border-l border-gray-100" style={{ width: 200 }}>
              <div className="p-2.5 border-b border-gray-100">
                <div className="relative">
                  <svg className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    value={optionSearch}
                    onChange={(e) => setOptionSearch(e.target.value)}
                    placeholder={`Search ${activeCategory.label}…`}
                    className="w-full pl-6 pr-2 h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white text-gray-700 placeholder-gray-400 transition-colors"
                  />
                </div>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                {activeOptions.map((option) => {
                  const checked = isSelected(activeCategory.key, option.key);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => handleOptionToggle(activeCategory.key, option.key)}
                    >
                      <div
                        className={`flex-shrink-0 w-[14px] h-[14px] rounded-[3px] border-[1.5px] flex items-center justify-center transition-all ${
                          checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {checked && (
                          <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2">
                            <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      {option.icon && (
                        <span className="flex-shrink-0 flex items-center size-5 rounded-full overflow-hidden">
                          {option.icon}
                        </span>
                      )}
                      <span
                        className="flex-1 text-left truncate"
                        style={option.color ? { color: option.color } : undefined}
                      >
                        {option.label}
                      </span>
                    </button>
                  );
                })}
                {activeOptions.length === 0 && (
                  <p className="px-3 py-4 text-[12px] text-gray-400 text-center">No options found</p>
                )}
              </div>
              {getCategoryCount(activeCategory.key) > 0 && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-[12px] text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors text-left"
                    onClick={() => handleRemoveFilter(activeCategory.key)}
                  >
                    Clear {activeCategory.label}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Divider — always in DOM, invisible when no filters, so flex layout never shifts */}
      <div className={`h-4 w-px flex-shrink-0 transition-colors ${appliedPills.length > 0 ? 'bg-gray-200' : 'bg-transparent'}`} />

      {/* Pills container — always a flex-1 child so the Filter button never moves */}
      <div className="flex items-center gap-1.5 overflow-x-auto flex-1 min-w-0" style={{ scrollbarWidth: 'none' }}>
        {appliedPills.map((pill) => (
          <FilterPill
            key={pill.key}
            label={pill.label}
            selectedValues={pill.selectedValues}
            onClick={() => openCategory(filters.find((f) => f.key === pill.key))}
            onRemove={() => handleRemoveFilter(pill.key)}
          />
        ))}
      </div>

      {/* Reset — always in DOM as flex-shrink-0, invisible when no filters */}
      <button
        type="button"
        className={`flex-shrink-0 h-7 px-2 rounded text-[11px] transition-colors whitespace-nowrap ${
          appliedPills.length > 0
            ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer'
            : 'text-transparent pointer-events-none'
        }`}
        onClick={handleClearAll}
        tabIndex={appliedPills.length > 0 ? 0 : -1}
      >
        Reset
      </button>
    </div>
  );
};

const mapStateProps = (state, props) => ({
  projectCustomFields: getWorkspaceProjectCustomFields(state, { projectId: props?.projectId }),
  isProjectCustomFieldsLoaded: isWorkspaceProjectCustomFieldsLoaded(state, { projectId: props?.projectId }),
  workspaceTags: state.workspaceReducer.workspaceTags,
  workspaceCustomFields: getWorkspaceCustomFields(state),
});

export default connect(mapStateProps)(FilterDropdown);
